<template>
    <form @submit.prevent="submit()" class="batch-action-form">
        <section class="section-wrapper container-fluid px-0 page-header">
            <h3 class="page-title">
                <slot name="title"></slot>
            </h3>

            <div class="adminbar-wrapper">
                <slot name="page-actions" :form-valid="true" :finished="finished"></slot>
            </div>

            <hr>
        </section>

        <section class="page-content">
            <div class="row" v-if="!finished">
                <div class="col-md-5 col-lg-4 col-xl-3">
                    <div class="card items-column">
                        <div class="card-header mx-3 px-0 py-3">
                            <div class="title">
                                <slot name="items-title" :count="Object.values(itemsSelected).filter(v => v).length" :total="items.length"></slot>
                            </div>
                        </div>
                        <div class="card-body p-0">
                            <ul class="list-items">
                                <li class="item" v-for="item in items" :class="{'inactive': itemsSelected[item.id] === false}">
                                    <div class="form-check">
                                        <input :id="'formCheck-'+item.id" class="form-check-input" type="checkbox" :value="item.id" v-model="itemsSelected[item.id]" />
                                        <label class="form-check-label" :for="'formCheck-'+item.id">
                                            <slot name="item-template" :item="item"></slot>
                                        </label>
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
                <div class="col-md-7 col-lg-8 col-xl-9">
                    <div class="card">
                        <div class="card-header mx-3 px-0 py-3">
                            <div class="title"><i class="fas fa-align-justify mr-2"></i><span>{{ trans('title') }}</span></div>
                        </div>
                        <div class="card-body bulk-action-form">

                            <div class="w-75">
                                <div class="form-group">
                                    <label for="document_groups">
                                        {{ trans('choose-action') }}:
                                    </label>
                                    <div class="input-group">
                                        <multiselect
                                            v-model="currentAction"
                                            :options="documentsActions"
                                            :close-on-select="true"
                                            :placeholder="trans('pick-item')"
                                            :allow-empty="true"
                                            deselect-label=""
                                            label="title"
                                            track-by="id"
                                            :multiple="false"
                                            id="document_groups"
                                            @input="fillTypesByAction()">
                                        </multiselect>
                                    </div>
                                </div>

                                <div class="form-group mb-4">
                                    <label for="document_groups">
                                        {{ trans('choose-document-types') }}:
                                    </label>
                                    <multiselect
                                        v-model="currentDocumentTypes"
                                        :options="documentsTypes"
                                        :close-on-select="false"
                                        :placeholder="trans('pick-items')"
                                        :allow-empty="true"
                                        deselect-label=""
                                        label="title"
                                        track-by="id"
                                        :multiple="true"
                                        id="document_types"
                                        @change=""
                                    ></multiselect>
                                </div>

                                <hr>

                                <div class="my-4">
                                    <div class="alert alert-warning text-dark font-weight-bold text-center d-flex align-items-center">
                                        <i class="fal fa-2x fa-fw fa-exclamation-triangle mr-3"></i>
                                        {{ trans('warning-generate-for-existing-intervals') }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="loading-box" v-show="loading && !finished">
                <div class="loading-inner">
                    <h2 class="mb-5 title">{{ trans('generating_data') }}&hellip;</h2>
                    <i class="fa fa-spin fa-circle-notch text-secondary"></i>
                </div>
            </div>

            <div class="finished-pane" v-if="finished">
                <div class="card page-card p-4">
                    <template v-if="finishedResult.status === true">
                        <h2 class="title mb-5" :class="this.finishedResult.skipsCount > 0 ? 'text-warning' : 'text-success'">
                            <i class="fal  mr-2" :class="this.finishedResult.skipsCount > 0 ? 'fa-exclamation-triangle' : 'fa-check-double'"></i>
                            {{ trans(this.finishedResult.skipsCount > 0 ? 'finished_with_errors' : 'finished_ok') }}
                        </h2>
                        <p class="text-center">{{ trans('success_msg') }}</p>
                        <p class="text-center" v-if="this.finishedResult.skipsCount > 0">{{ trans('some_were_skipped') }}</p>

                        <div class="d-flex justify-content-end mt-4" v-if="hasAnyGeneratedDocument">
                            <button @click="downloadAllAsZip()" type="button" class="btn btn-dark">
                                <i class="far fa-file-archive mr-1"></i> {{ trans('download-all-zip') }}
                            </button>
                        </div>

                        <table class="table w-100 mt-4">
                            <tr v-for="(docs, empId) in finishedResult.data">
                                <td class="border-bottom">
                                    <div class="d-flex align-items-center">
                                        <span class="badge badge-warning contract-type mr-2">{{ getEmployeeContractBadge(empId) }}</span>
                                        {{ getEmployeeName(empId) }}
                                    </div>
                                </td>
                                <td style="width: 30px;">
                                    <a :href="'/employees/'+empId+'/show'" v-b-tooltip.hover.left :title="trans('link-employees-overview')" class="btn btn-sm btn-outline-success border-0">
                                        <i class="far fa-fw fa-eye"></i>
                                    </a>
                                </td>
                                <td class="border-bottom">
                                    <div v-if="docs.success.length === 0" class="text-right">
                                        <span v-b-tooltip.hover.right :title="trans('label-no-generated-document')">
                                            <i class="fas fa-minus-circle text-danger p-2"></i>
                                        </span>
                                    </div>
                                    <div v-else class="d-flex align-items-center justify-content-end">
                                        <span v-if="docs.skipped.length > 0" :id="'finish-pane-tooltip-skip-'+empId">
                                            <i class="fas fa-exclamation-triangle text-warning p-2 mr-2"></i>
                                        </span>
                                        <b-tooltip :target="'finish-pane-tooltip-skip-'+empId" placement="left">
                                            {{ trans('label-not-generated') }}:
                                            <ul class="p-0 pl-3 m-0 text-left" style="color: white;">
                                                <li v-for="typeId in docs.skipped">{{ getDocTypeName(typeId) }}</li>
                                            </ul>
                                        </b-tooltip>

                                        <button @click="downloadEmployeeDocuments(empId, docs.success)" type="button" class="btn btn-dark">
                                            <i class="far fa-file-pdf"></i> PDF
                                            <small>({{ docs.success.length }})</small>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        </table>
                    </template>
                    <template v-else-if="finishedResult.status === false">
                        <h2 class="title mb-5 text-danger"><i class="fal fa-times-hexagon mr-2"></i> {{ trans('processing_failed') }}</h2>
                        <p class="text-center font-weight-bold">{{ trans('problem_contact_support') }}</p>
                    </template>
                </div>
            </div>

            <modal
                name="submit-confirm-modal"
                class="modal--translation modal--hide-on-loading"
                v-cloak
                height="auto"
                :scrollable="true"
                :adaptive="true"
                :pivot-y="0.25"
            >
                <div class="modal-header p-0">
                    <button @click="closeSubmmitModal()" type="button" class="close close-modal mr-2"><i class="fal fa-fw fa-times"></i></button>
                    <h5 class="modal-title m-0 mb-2"><i class="fa fa-fw fa-question mr-1"></i> {{ trans('modal_submit_title') }}</h5>
                </div>

                <div class="modal-body py-5">
                    <h4 class="text-danger text-center p-0 m-0">{{ trans('modal_submit_confirm') }}</h4>
                </div>

                <div class="modal-footer px-0 justify-content-center">
                    <b-button type="button" variant="primary" @click="send()" :class="{'disabled': loading }"><i class="fa fa-check mr-2"></i> {{ trans('submit') }}</b-button>
                    <b-button type="button" variant="light" @click="closeSubmmitModal()"><i class="fa fa-times mr-2 text-dark"></i> {{ trans('cancel') }}</b-button>
                </div>
            </modal>

        </section>
    </form>
</template>

<script>
    import TranslationsUtils from "../app-components/TranslationsUtils";
import _moment from "moment";

    export default {
        mixins: [TranslationsUtils],
        props: {
            items: { type: Array },
            documentsActions: { type: Array },
            documentsTypes: { type: Array },
            saveUrl: { type: String },
            downloadPdfUrl: { type: String },
            zipLoaderUrl: { type: String },
            zipExportUrl: { type: String }
        },

        data() {
            return {
                itemsSelected: this.items.reduce((acc, item) => {
                                    acc[item.id] = true;
                                    return acc;
                               }, {}),
                currentAction: null,
                currentDocumentTypes: [],
                loading: false,
                finished: false,
                finishedResult: {
                    status: true,
                    success: 20,
                    failed: 0,
                    errors: [],
                    skipsCount: 0,
                    data: []
                }
            };
        },

        computed: {
            hasAnyGeneratedDocument() {
                return Object.values(this.finishedResult.data || {})
                    .some(docs => docs.success && docs.success.length > 0);
            }
        },

        methods: {
            fillTypesByAction() {
                let action = this.documentsActions.find(item => item.id === this.currentAction.id);
                this.currentDocumentTypes = this.documentsTypes.filter(item => action.types.includes(item.id));
            },

            getEmployeeName(empId) {
                let emp = this.items.find(item => item.id == empId);
                return emp ? emp.name : '-';
            },

            getEmployeeContractBadge(empId) {
                let emp = this.items.find(item => item.id == empId);
                return emp ? emp.contract : '?';
            },

            getDocTypeName(typeId) {
                let docType = this.documentsTypes.find(item => item.id == typeId)
                return docType ? docType.title : '? (ID: '+typeid+')';
            },

            submit() {
                this.loading = false;
                this.$modal.show('submit-confirm-modal');
            },

            closeSubmmitModal() {
                this.$modal.hide('submit-confirm-modal');
            },

            send() {
                this.loading = true;
                // close the confirm modal right away so the global ".loading" toggle
                // (cleared by unrelated background requests like /maintenance-notification)
                // cannot re-reveal an already confirmed, still-open modal during generation
                this.closeSubmmitModal();

                axios.post(this.saveUrl, {
                    'items': this.itemsSelected,
                    'templates': this.currentDocumentTypes.map(item => item.id), // TODO
                })
                    .then((response) => {
                        try {
                            this.showResults(response.data);
                            this.loading = false;
                        } catch (err) {
                            //throw err; // nech prejde do .catch()
                        }
                    })
                    .catch(error => {
                        if (error.response.status === 422) {
                            this.$notify({
                              type: 'error',
                              title: this.trans('error'),
                              text: this.trans('fields_validation_error')
                            });

                            if(error.response.data.errors) {
                                // TODO
                            }
                        } else {
                            this.$notify({
                              type: 'error',
                              title: this.trans('error'),
                              text: error.response.data.message ? error.response.data.message : this.trans('unknown_error')
                            });
                        }

                        this.closeSubmmitModal();
                        this.loading = false;
                    });
            },

            downloadEmployeeDocuments(empId, ids) {
                if (!ids || !ids.length) {
                    this.$notify({
                        type: 'error',
                        title: this.trans('error'),
                        text: this.trans('no_documents_selected'),
                    });
                    return;
                }

                axios({
                    method: 'post',
                    url: this.downloadPdfUrl,
                    responseType: 'arraybuffer',
                    data: { document_ids: ids }
                })
                    .then((response) => {
                        try {
                            this.servePdf(response.data, empId);
                        } catch (error) {
                            console.error(error);

                            this.$notify({
                                type: 'error',
                                title: this.trans('error'),
                                text: this.trans('unknown_error'),
                            });
                        }
                    })
                    .catch(error => {
                        if (error.response && error.response.status === 422) {
                            this.$notify({
                                type: 'error',
                                title: this.trans('error'),
                                text: this.trans('fields_validation_error')
                            });
                        } else {
                            this.$notify({
                                type: 'error',
                                title: this.trans('error'),
                                text: error.response && error.response.data && error.response.data.message
                                    ? error.response.data.message
                                    : this.trans('unknown_error')
                            });
                        }
                    });
            },

            servePdf(arrayBuffer, empId) {
                // vytvoríme Blob z prijatých dát
                const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `dokumenty_${empId}_${_moment().format('YYYY-MM-DD-HH-mm')}.pdf`;

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                window.URL.revokeObjectURL(url);
            },

            downloadAllAsZip() {
                let ids = Object.values(this.finishedResult.data || {})
                    .reduce((acc, docs) => acc.concat(docs.success || []), []);

                if (!ids.length) {
                    this.$notify({
                        type: 'error',
                        title: this.trans('error'),
                        text: this.trans('no_documents_selected'),
                    });
                    return;
                }

                let exportUrl = this.zipExportUrl + '?ids=' + ids.join(',');
                let loaderUrl = this.zipLoaderUrl + '?url=' + encodeURIComponent(exportUrl);

                window.open(loaderUrl, '_blank');
            },

            showResults(data) {
                this.finished = true;
                this.finishedResult = data;
            },


        }
    }
</script>


<style lang="scss">
    .btn-primary:disabled {
        opacity: 0.5;
        border-color: #4273fa !important;
        background: #4273fa !important;
    }

    .batch-action-form {
        .card-header, .page-title {
            user-select: none;
        }

        section.page-header .adminbar-wrapper {
            right: 0;
        }

        .items-column {
            margin-bottom: 0 !important;

            .card-body {
                max-height: calc(100vh - 200px);
                min-height: 200px;
                overflow: auto;
            }
        }

        .list-items {
            list-style-type: none;
            padding: 0;

            .item {
                padding: 2px 0px;
                border-bottom: solid #eef1f3 1px;
                margin: 0 15px;
                user-select: none;

                .form-check {
                    padding: 6px 0px;
                    margin-bottom: 0;

                    .form-check-label {
                        margin-bottom: 0;
                    }
                }

                .label {
                    display: flex;
                    align-items: center;
                }

                &.inactive {
                    opacity: 0.7;
                }
            }
        }

        .bulk-action-form {
            .subtitle {
                font-size: 1rem;
                margin: 0 0 1.5rem 0;
                text-align: center;
            }
            .help-icon {
                font-size: 1rem;
                margin-left: 1rem;
            }

            tbody {
                .input-group-prepend {
                    .btn {
                        border-radius: 0px !important;
                    }

                    .fa-chevron-down {
                        color: #444444;
                        font-size: 0.6rem;
                        margin-left: 0.6rem;
                    }
                }
                .input-group-addon {
                    border-radius: 0px !important;
                }


                .validation-indicator {
                    display: none;
                }

                .field-validation-error {
                    .validation-indicator {
                        display: inline-block;
                        margin-left: 1.8rem;

                        i {
                            font-size: 1.2rem;
                        }
                    }
                }

                .input-group.is-invalid {
                    .form-control, .input-group-addon {
                        border: 1px solid #dc3545;
                    }
                    .input-group-addon:not(:last-child) {
                        border-right: 0px !important;
                    }
                }

            }
        }

        .basic-form-items {
            .input-group-addon {
                border-radius: 0px !important;
            }
        }

        .bulk-action-form tbody .input-group, .bulk-action-form tbody .form-select, .basic-form-items .form-control {
            max-width: 250px;
        }

        .loading-inner, .countdown-inner {
            background: white;
            padding: 40px;
            box-shadow: 0px 0px 4px 1px #EEE;
            border-radius: 6px;

            h2 { color: #555; }
        }

        .table--admin-listing {
            td:last-of-type {
                min-width: auto;
                display: table-cell;
                justify-content: initial;
            }
        }

        .finished-pane {
            margin: 2rem auto;
            max-width: 600px;
            font-size: 0.9rem;

            .title {
                font-size: 1.2rem;
            }

            table {
                width: 300px;

                td {
                    max-width: 50%;
                    line-height: 150%;
                }
            }

            ul li { margin-bottom: 0.3rem; }

        }

        .loading-box {
            position: absolute;
            top: 0;
            left: 0;
            background: rgba(255,255,255,0.6);
            z-index: 10;

            .loading-inner {
                text-align: center;
                z-index: 10;

                .title {
                    font-size: 1.3rem;
                    color: #4273FA;
                }
            }
        }
    }
</style>



// WEBPACK FOOTER //
// resources/assets/admin/js/components/BatchActionDocumentsForm.vue